// mapd-churn-analytics.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'stats',
  tag: 'Data & Research',
  stats: [
    { number: '15.6%', label: 'Churn in Year 1', color: 'red' },
    { number: '49%', label: 'Churn by Year 5', color: 'amber' },
    { number: '21%', label: 'Switch in 2 Years', color: 'blue' },
    { number: '1 in 10', label: 'Switch Annually', color: 'purple' }
  ],

  html: `
<p>If you want to get serious about retention, you need to get serious about data. Not feelings, not hunches—actual numbers that show you where churn happens, why it happens, and who's most at risk.</p>

<p>This analysis pulls together the most important MAPD churn research from CMS, academic studies, and industry sources. Use it to benchmark yourself and understand the patterns you're fighting against.</p>

<h2>National Churn Benchmarks</h2>

<p>Let's establish baseline expectations for Medicare Advantage churn:</p>

<h3>Short-Term Churn (Year 1)</h3>
<p>According to research published in the American Journal of Managed Care, <strong>15.6% of new MA enrollees change insurance within their first year.</strong> That's roughly 1 in 6 new clients gone before their first anniversary.</p>

<p>This first-year vulnerability is critical. Your onboarding process and early touchpoints directly impact whether new clients become retained clients or first-year churn statistics.</p>

<h3>Medium-Term Churn (Years 2-5)</h3>
<p>The same research found that <strong>49.2% of new enrollees had changed insurance by the 5-year mark</strong>—nearly half. The cumulative effect compounds year over year:</p>
<ul>
<li>Year 1: ~15.6% cumulative churn</li>
<li>Year 2: ~25% cumulative churn</li>
<li>Year 3: ~33% cumulative churn</li>
<li>Year 5: ~49% cumulative churn</li>
</ul>

<h3>Annual Switching Rate</h3>
<p>On average, about <strong>1 in 10 MA enrollees switch plans each year</strong>. This is the steady-state churn rate for established clients—lower than first-year churn but still significant.</p>

<h3>Two-Year Window</h3>
<p>Commonwealth Fund research found that <strong>21% of MA enrollees reported switching coverage in a two-year period</strong>. That's roughly 10.5% per year on average, consistent with the 1-in-10 annual figure.</p>

<h2>Churn by Population Segment</h2>

<p>Not all populations churn equally. Understanding segment-specific rates helps you prioritize retention efforts.</p>

<h3>Dual Eligible vs. Non-Dual</h3>
<p>A JAMA Health Forum study of 82+ million individuals found significant differences:</p>
<ul>
<li><strong>Non-dual beneficiaries:</strong> 48% left their MA contract after 5 years</li>
<li><strong>Dual beneficiaries:</strong> 53% left their MA contract after 5 years</li>
</ul>
<p>Dual eligibles churn about 10% more than non-duals over a 5-year period. This population requires more intensive retention effort.</p>

<h3>New Enrollees vs. Established Clients</h3>
<p>First-year clients are significantly higher risk:</p>
<ul>
<li>Year 1 churn: ~15.6%</li>
<li>Subsequent year churn: ~10%</li>
</ul>
<p>New enrollees need more touchpoints and closer attention than established clients.</p>

<h3>Health Status Impact</h3>
<p>Research indicates that <strong>beneficiaries with greater health needs have higher annual disenrollment rates</strong>. Sicker clients are more likely to experience plan friction (network issues, prior auth delays, coverage gaps) that drives switching.</p>

<h2>Churn Drivers: What the Data Says</h2>

<p>CMS conducts a Disenrollment Reasons Survey to understand why people leave their plans. Key findings:</p>

<h3>Cost Factors</h3>
<ul>
<li>Premium increases trigger shopping behavior</li>
<li>Out-of-pocket cost surprises drive disenrollment</li>
<li>Cost is consistently the #1 self-reported reason for switching</li>
</ul>

<h3>Quality and Access Factors</h3>
<ul>
<li>Provider network changes (doctor leaving network)</li>
<li>Pharmacy network limitations</li>
<li>Prior authorization delays and denials</li>
<li>Service quality concerns</li>
<li>Star rating declines correlate with higher disenrollment</li>
</ul>

<h3>Benefits Factors</h3>
<ul>
<li>Benefit reductions year-over-year</li>
<li>Formulary changes affecting medications</li>
<li>Perceived better benefits elsewhere</li>
</ul>

<h2>Plan-Level Variation</h2>

<p>Churn rates vary significantly by plan. The JAMA study noted "substantial variation by plan" in disenrollment patterns. This means:</p>
<ul>
<li>Some plans inherently retain better than others</li>
<li>Plan-specific factors (network stability, Star ratings, premium trends) matter</li>
<li>Knowing which plans in your book have the highest churn helps you prioritize</li>
</ul>

<p>Track your own retention by plan. If Plan X consistently churns at 25% while Plan Y churns at 10%, that tells you something about both the plan and the clients you're enrolling in it.</p>

<h2>Timing Patterns</h2>

<p>When does churn happen?</p>

<h3>AEP Concentration</h3>
<p>The majority of voluntary plan switching occurs during AEP (October 15 - December 7). This is when clients have maximum flexibility and maximum exposure to competitor marketing.</p>

<h3>OEP Window</h3>
<p>January-March sees a secondary switching period for MA clients who can make one change during OEP. First-year clients are especially vulnerable during this window.</p>

<h3>SEP Activity</h3>
<p>Dual eligibles can switch quarterly, so their churn is spread throughout the year. Clients with qualifying life events (moves, lost coverage, etc.) can switch anytime.</p>

<h2>Predictive Indicators</h2>

<p>Based on the research, these factors predict higher churn risk:</p>

<ul>
<li><strong>First-year status:</strong> Highest-risk period for any client</li>
<li><strong>Dual eligibility:</strong> Higher baseline churn</li>
<li><strong>High health needs:</strong> More plan friction opportunities</li>
<li><strong>Plan instability:</strong> Plans with declining Stars or significant changes</li>
<li><strong>Low contact frequency:</strong> Clients you haven't touched recently</li>
<li><strong>Previous complaints:</strong> Clients who've had unresolved issues</li>
<li><strong>Network changes:</strong> Clients whose doctors are leaving network</li>
<li><strong>Formulary changes:</strong> Clients whose medications are affected</li>
</ul>

<h2>Applying the Data to Your Book</h2>

<p>Use these benchmarks to evaluate your own performance:</p>

<h3>Are You Above or Below Average?</h3>
<ul>
<li>If your first-year retention is better than 85%, you're outperforming average</li>
<li>If your 5-year retention is better than 51%, you're outperforming average</li>
<li>If your annual retention is better than 90%, you're outperforming average</li>
</ul>

<h3>Where Are You Losing?</h3>
<ul>
<li>Calculate segment-specific retention: new vs. established, dual vs. non-dual, by plan</li>
<li>Identify your worst-performing segments</li>
<li>Focus retention efforts where the data shows the biggest problems</li>
</ul>

<h3>What's Your Trend?</h3>
<ul>
<li>Is retention improving or declining year over year?</li>
<li>Are the same patterns repeating?</li>
<li>What changed when retention changed?</li>
</ul>

<h2>The Retention Opportunity</h2>

<p>Here's the bottom line: national averages show significant churn that creates opportunity. If 15% of new enrollees leave in year 1 nationally, an agent who achieves 10% year-1 churn is dramatically outperforming.</p>

<p>The data shows that churn is predictable and preventable. The agents who use this data—who identify at-risk clients, who prioritize based on risk factors, who intervene before clients leave—retain dramatically better than those who don't.</p>

<p>Data-driven retention isn't a nice-to-have. It's how you win.</p>
  `,

  faqs: [
    {
      question: "What's the average first-year churn rate for new MA enrollees?",
      answer: "Research shows about 15.6% of new MA enrollees change insurance within their first year. This makes the first year the highest-risk period for any new client, requiring extra touchpoints and attention."
    },
    {
      question: "How does dual eligibility affect churn rates?",
      answer: "Dual eligible beneficiaries churn about 10% more than non-duals over a 5-year period (53% vs. 48% leaving their contract). They also have year-round SEP eligibility, meaning they can switch quarterly—not just during AEP."
    },
    {
      question: "What are the main reasons people leave their MA plans?",
      answer: "According to CMS survey data: cost factors (premiums, out-of-pocket surprises) are #1, followed by provider/network issues (doctors leaving network), service quality concerns, and benefit changes (formulary or benefit reductions)."
    },
    {
      question: "Does churn vary by plan?",
      answer: "Yes, significantly. Research notes 'substantial variation by plan' in disenrollment patterns. Track retention by plan in your own book—if certain plans consistently churn more, that's valuable intelligence for both retention efforts and enrollment decisions."
    },
    {
      question: "What factors predict which clients are most likely to churn?",
      answer: "Key predictors: first-year status, dual eligibility, high health needs, plan instability (declining Stars or changes), low contact frequency, previous complaints or issues, and upcoming network or formulary changes affecting the client."
    }
  ],

  sources: [
    {
      title: "Turnover Among New Medicare Advantage Enrollees May Be Greater Than Perceived",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9586458/",
      publisher: "AJMC/PMC",
      accessedDate: "2024-12-10"
    },
    {
      title: "Trends in Cumulative Disenrollment in the Medicare Advantage Program, 2011-2020",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10457718/",
      publisher: "JAMA Health Forum/PMC",
      accessedDate: "2024-12-10"
    },
    {
      title: "Open Enrollment Allows Medicare Beneficiaries to Change Coverage",
      url: "https://www.commonwealthfund.org/blog/2024/open-enrollment-allows-medicare-beneficiaries-change-coverage",
      publisher: "Commonwealth Fund",
      accessedDate: "2024-12-10"
    },
    {
      title: "Medicare Advantage and Prescription Drug Plan Disenrollment Reasons Survey",
      url: "https://www.cms.gov/research-statistics-data-and-systems/research/cahps/mapdp_disenrollmentsurvey",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Data-driven retention requires tools that turn these insights into action. Pro IRP calculates risk scores based on the factors research shows predict churn, so you know exactly which clients need attention—not just national averages, but your specific book.`
};

export default content;
